import { useCallback, useSyncExternalStore } from 'react';
import { setUiSoundEnabled, unlockUiSound } from '../services/ui-sound';

const STORAGE_KEY = 'anidex-ui-sound';
const listeners = new Set<() => void>();
let fallbackEnabled: boolean | null = null;
let gestureBound = false;

function defaultEnabled(): boolean {
  if (typeof window === 'undefined') return true;
  return !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function readEnabled(): boolean {
  try {
    const value = localStorage.getItem(STORAGE_KEY);
    if (value === 'on') return true;
    if (value === 'off') return false;
  } catch {
    if (fallbackEnabled !== null) return fallbackEnabled;
  }
  return defaultEnabled();
}

function notify() {
  for (const listener of listeners) listener();
}

function apply(enabled: boolean) {
  setUiSoundEnabled(enabled);
  notify();
}

function bindGestureUnlock() {
  if (gestureBound || typeof window === 'undefined') return;
  gestureBound = true;
  const unlock = () => {
    unlockUiSound();
    window.removeEventListener('pointerdown', unlock);
    window.removeEventListener('keydown', unlock);
  };
  window.addEventListener('pointerdown', unlock, { once: true, passive: true });
  window.addEventListener('keydown', unlock, { once: true });
}


export function watchUiSound() {
  apply(readEnabled());
  bindGestureUnlock();
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY) apply(readEnabled());
  });
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useUiSound() {
  const enabled = useSyncExternalStore(subscribe, readEnabled, (): boolean => true);

  const setEnabled = useCallback((next: boolean) => {
    try {
      localStorage.setItem(STORAGE_KEY, next ? 'on' : 'off');
    } catch {
      fallbackEnabled = next;
    }
    if (next) unlockUiSound();
    apply(next);
  }, []);

  const toggle = useCallback(() => {
    setEnabled(!readEnabled());
  }, [setEnabled]);

  return { enabled, setEnabled, toggle };
}
